import { useMutation, useQuery } from 'react-query';
import { request, request_v2, requestXlsxFileDownload } from '../utils/axios-utils';
import { data as PendencyData } from './planningPendency.json.js';

const GetPlanningResource = async (companyCode, type) => {
    const { data } = await request_v2({
        method: 'get',
        url: `/shootPlanning/GetPlanningResource`,
        params: {
            companyCode,
            type,
        },
    });
    return data;
};

const GetCSVFieldsList = async companyCode => {
    const { data } = await request_v2({
        method: 'get',
        url: `/shootPlanning/getCSVFieldsList/${companyCode}`,
    });
    return data;
};

const UpdatePlanningResource = async (companyCode, type, planningResource) => {
    const { data } = await request_v2({
        method: 'post',
        url: '/shootPlanning/updatePlanningResource',
        body: {
            companyCode,
            type,
            planningResource,
        },
    });
    return data;
};

const UpdateCSVFields = async (companyCode, fields) => {
    const { data } = await request_v2({
        method: 'post',
        url: '/shootPlanning/updateCSVFieldsList',
        body: {
            companyCode,
            fields,
        },
    });
    return data;
};

const GetAllCSVFields = async companyCode => {
    const { data } = await request_v2({
        method: 'get',
        url: `/shootPlanning/getCSVFieldsListAll/${companyCode}`,
    });
    return data;
};

const GetSelectedCSVFields = async companyCode => {
    const { data } = await request_v2({
        method: 'get',
        url: `/shootPlanning/getCSVFieldsList/${companyCode}`,
    });
    return data;
};

const GetPlanningPendency = async (
    companyCode,
    selectedModels,
    selectedBays,
    ExcludeLinks,
    splitByShootType,
    splitByOrder,
    includePendingAtRawQC,
    showOnlyDDApproved,
    showCSA,
    showSLAPending,
    splitByAssignedModel,
    includeSpecialCategory,
    splitByPresets,
    splitByDaysSinceInbound,
    onSuccess
) => {
    // return PendencyData;
    console.log({
        companyCode,
        selectedModels,
        selectedBays,
        ExcludeLinks,
        splitByShootType,
        splitByOrder,
        includePendingAtRawQC,
        showOnlyDDApproved,
        showCSA,
        showSLAPending,
        splitByAssignedModel,
        includeSpecialCategory,
        splitByPresets,
        splitByDaysSinceInbound,
    });
    if (!companyCode) {
        return null;
    }
    const { data } = await request_v2({
        method: 'post',
        url: '/shootPlanning/getPlanningPendency',
        body: {
            companyCode: companyCode,
            selectedModels: selectedModels,
            selectedBays: selectedBays,
            ExcludeLinks: ExcludeLinks,
            splitByShootType: splitByShootType,
            includePendingAtRawQC: includePendingAtRawQC,
            showOnlyDDApproved: showOnlyDDApproved,
            showCSA: showCSA,
            splitByOrder: splitByOrder,
            showSLAPending: showSLAPending,
            splitByPresets: splitByPresets,
            splitByAssignedModel: splitByAssignedModel,
            includeSpecialCategory: includeSpecialCategory,
            splitByDaysSinceInbound: splitByDaysSinceInbound,
        },
    });
    onSuccess();
    return data;
};

const GetPlanningPendencyForBSTWithCSVData = async (
    ExcludeLinks,
    companyCode,
    includePendingAtRawQC,
    includeSpecialCategory,
    orderIds,
    showOnlyDDApproved,
    showCSA
) => {
    console.log({
        ExcludeLinks,
        companyCode,
        includePendingAtRawQC,
        includeSpecialCategory,
        orderIds,
        showOnlyDDApproved,
        showCSA,
    });
    if (!companyCode) {
        return null;
    }
    const response = await requestXlsxFileDownload({
        method: 'post',
        url: '/shootPlanning/GetPlanningPendencyForBSTWithCSVData',
        data: {
            ExcludeLinks: ExcludeLinks,
            companyCode: companyCode,
            includePendingAtRawQC: includePendingAtRawQC,
            includeSpecialCategory: includeSpecialCategory,
            orderIds: orderIds,
            showOnlyDDApproved: showOnlyDDApproved,
            showCSA: showCSA,
        },
    });
    // Extract the filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'report.xlsx';
    // if (contentDisposition) {
    //     const match = contentDisposition.match(/filename="?(.+)"?/);
    //     if (match && match[1]) {
    //         fileName = match[1];
    //     }
    // }

    // Create a link element to trigger the download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // Set the filename
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);

    // Revoke the object URL after the download is triggered
    window.URL.revokeObjectURL(url);
    // return response;
};

const CreateMergeGuidelineFromPlanning = async payload => {
    const { data } = await request_v2({
        method: 'post',
        url: '/guideline/CreateMergeGuidelineFromPlanning',
        body: payload,
    });
    return data;
};

const GetSkuListInPlanning = async skuIds => {
    console.log(skuIds);
    const { data } = await request_v2({
        method: 'post',
        url: '/sku/info ',
        body: {
            skuIds,
        },
    });
    return data;
};

export const useGetPlanningResource = (companyCode, type) =>
    useQuery(['PlanningResource', companyCode, type], () => GetPlanningResource(companyCode, type), { refetchOnWindowFocus: false });

export const useGetCSVFieldsList = companyCode =>
    useQuery(['CSVFieldList', companyCode], () => GetCSVFieldsList(companyCode), { refetchOnWindowFocus: false });

export const useGetAllCSVFields = companyCode =>
    useQuery(['AllCSVFields', companyCode], () => GetAllCSVFields(companyCode), {
        refetchOnWindowFocus: false,
    });

export const useGetSelectedCSVFields = companyCode =>
    useQuery(['SelectedCSVFields', companyCode], () => GetSelectedCSVFields(companyCode), {
        refetchOnWindowFocus: false,
    });

export const useUpdatePlanningResource = () =>
    useMutation({
        mutationFn: async ([companyCode, type, placeholderModels]) => {
            return await UpdatePlanningResource(companyCode, type, placeholderModels);
        },
    });

export const useUpdateCSVFields = () =>
    useMutation({
        mutationFn: async ([companyCode, fields]) => {
            return await UpdateCSVFields(companyCode, fields);
        },
    });

export const useGetSkuListInPlanning = (onSuccess, onError) => {
    return useMutation(GetSkuListInPlanning, {
        onSuccess: onSuccess,
        onError: onError,
    });
};

export const useGetPlanningPendencyForBSTWithCSVData = () =>
    useMutation({
        mutationFn: async ([ExcludeLinks, companyCode, includePendingAtRawQC, includeSpecialCategory, orderIds, showOnlyDDApproved, showCSA]) => {
            return await GetPlanningPendencyForBSTWithCSVData(
                ExcludeLinks,
                companyCode,
                includePendingAtRawQC,
                includeSpecialCategory,
                orderIds,
                showOnlyDDApproved,
                showCSA
            );
        },
    });

// export const useGetPlanningPendencyForBSTWithCSVData = (
//     ExcludeLinks,
//     companyCode,
//     includePendingAtRawQC,
//     includeSpecialCategory,
//     orderIds,
//     showOnlyDDApproved,
//     onSuccess
// ) =>
//     useQuery(['planningDataForBST', ExcludeLinks, companyCode, includePendingAtRawQC, includeSpecialCategory, orderIds, showOnlyDDApproved], () =>
//         GetPlanningPendencyForBSTWithCSVData(
//             ExcludeLinks,
//             companyCode,
//             includePendingAtRawQC,
//             includeSpecialCategory,
//             orderIds,
//             showOnlyDDApproved,
//             onSuccess
//         )
//     );

export const useGetPlanningPendency = (
    companyCode,
    selectedModels,
    selectedBays,
    ExcludeLinks,
    isQueryEnabled = false,
    splitByShootType,
    splitByOrder,
    includePendingAtRawQC,
    showOnlyDDApproved,
    showCSA,
    showSLAPending,
    splitByAssignedModel,
    includeSpecialCategory,
    splitByPresets,
    splitByDaysSinceInbound,
    onSuccess
) =>
    useQuery(
        [
            'planningData',
            companyCode,
            selectedModels,
            selectedBays,
            ExcludeLinks,
            splitByShootType,
            splitByOrder,
            includePendingAtRawQC,
            showOnlyDDApproved,
            showCSA,
            showSLAPending,
            splitByAssignedModel,
            includeSpecialCategory,
            splitByPresets,
            splitByDaysSinceInbound,
        ],
        () =>
            GetPlanningPendency(
                companyCode,
                selectedModels,
                selectedBays,
                ExcludeLinks,
                splitByShootType,
                splitByOrder,
                includePendingAtRawQC,
                showOnlyDDApproved,
                showCSA,
                showSLAPending,
                splitByAssignedModel,
                includeSpecialCategory,
                splitByPresets,
                splitByDaysSinceInbound,
                onSuccess
            ),
        {
            enabled: !!isQueryEnabled,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );

export const useCreateMergeGuidelineFromPlanning = () =>
    useMutation({
        mutationFn: async payload => {
            return await CreateMergeGuidelineFromPlanning(payload);
        },
    });
